import type { EnrichedAuditEvent } from '@eppendorf/vnls-audit-event-client';
import {
  DeviceEventHit,
  GetDeviceEventsBody,
  GetDeviceEventsResponse,
  GetDevicesBySerialNumbersInput,
  GetDevicesBySerialNumbersResponse,
  GetDevicesResponse,
  GetTelemetryHistoryResponse,
} from '@eppendorf/vnls-device-service-utils';
import type { IncidentListDTO } from '@eppendorf/vnls-notifications-utils';
import { INCIDENT_PATTERNS } from '@eppendorf/vnls-notifications-utils';
import type {
  AuditTrailReportOutput,
  StabilityReportOutput,
} from '@eppendorf/vnls-reporting-service-utils';
import type { PaginatedResponse, User } from '@eppendorf/vnls-user-tenant-utils';
import { UserStatus } from '@eppendorf/vnls-user-tenant-utils';
import { useQuery } from '@tanstack/react-query';

import { fetcher } from '$shared/fetch';
import {
  AUDIT_BASE_PATH,
  AUDIT_TRAIL_REPORTS_BASE_PATH,
  createPathWithQueryParams,
  DEVICES_BY_SERIAL_NUMBERS_PATH,
  DEVICE_EVENTS_V2_BASE_PATH,
  DEVICE_SERVICE_BASE_PATH,
  INCIDENTS_BASE_PATH,
  STABILITY_REPORTS_BASE_PATH,
  USERS_BASE_PATH,
} from '$shared/utils/api-paths';

import type {
  AlertIncident,
  DashboardDeviceMetadata,
  DashboardDeviceStatus,
  DashboardDeviceStatusSummary,
  DashboardFleetStatus,
  DashboardHealthDeviceState,
  DashboardLabHealth,
  DashboardMonitoringHealth,
  LatestReport,
  PendingInvitation,
  RecentDeviation,
  RecentDeviations,
} from './dashboard.types';

export const dashboardQueryKeys = {
  labHealth: ['dashboard', 'labHealth'],
  deviceStatusSummary: ['dashboard', 'deviceStatusSummary'],
  fleetStatus: ['dashboard', 'fleetStatus'],
  recentDeviations: ['dashboard', 'recentDeviations'],
  incidents: ['dashboard', 'incidents'],
  reports: ['dashboard', 'reports'],
  latestAuditTrail: ['dashboard', 'latestAuditTrail'],
  pendingInvitations: ['dashboard', 'pendingInvitations'],
  deviceHistory: (manufacturer: string, serialNumber: string, telemetryId: string) => [
    'dashboard',
    'deviceHistory',
    manufacturer,
    serialNumber,
    telemetryId,
  ],
};

const REFETCH_INTERVAL_MS = {
  fast: 10_000,
  default: 30_000,
  slow: 60_000,
} as const;

const RECENT_DEVIATIONS_LIMIT = 5;
const RECENT_DEVIATIONS_WINDOW_MS = 24 * 60 * 60 * 1000;
const RECENT_DEVIATION_TAGS = ['error', 'alert', 'warning', 'alarm', 'warn'];
const DEVIATION_TIMELINE_PAGE_SIZE = 500;
const LAB_HEALTH_PAGE_SIZE = 500;
const DEVICE_HISTORY_WINDOW_MS = 60 * 60 * 1000;

const HEALTHY_ACTIVITY_STATES = new Set([
  'Idle',
  'Done',
  'ManuallyRunning',
  'AutomaticallyRunning',
  'Paused',
  'Hold',
  'Running',
  'Preparing',
  'Online',
]);
const ATTENTION_ACTIVITY_STATES = new Set(['Error', 'Offline', 'Unreachable']);
const ATTENTION_ALERT_STATES = new Set(['Warn', 'Alert']);

const INCIDENT_SEVERITY_PRIORITY: Record<AlertIncident['severity'], number> = {
  error: 4,
  alert: 3,
  warning: 2,
  info: 1,
};

function normalizeIncidentSeverity(level?: string): AlertIncident['severity'] {
  const normalizedLevel = level?.toLowerCase();
  if (
    normalizedLevel === 'error' ||
    normalizedLevel === 'alert' ||
    normalizedLevel === 'warning'
  ) {
    return normalizedLevel;
  }
  return 'info';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDeviceLocation(location?: {
  site?: string;
  building?: string;
  room?: string;
  position?: string;
}): string | undefined {
  const parts = [location?.site, location?.building, location?.room, location?.position]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(' / ') : undefined;
}

export async function fetchDashboardDeviceMetadata(
  serialNumbers: string[],
): Promise<Map<string, DashboardDeviceMetadata>> {
  const uniqueSerialNumbers = [...new Set(serialNumbers.filter(Boolean))];
  if (uniqueSerialNumbers.length === 0) return new Map();

  try {
    const response = await fetcher.post<
      GetDevicesBySerialNumbersInput,
      GetDevicesBySerialNumbersResponse
    >(DEVICES_BY_SERIAL_NUMBERS_PATH, { serialNumbers: uniqueSerialNumbers });

    return new Map(
      response.data.map((device) => [
        device.serialNumber,
        {
          manufacturer: device.manufacturer,
          serialNumber: device.serialNumber,
          name: device.name || device.serialNumber,
          location: formatDeviceLocation(device.location),
        },
      ]),
    );
  } catch {
    return new Map();
  }
}

function getDeviationSeverity(event: DeviceEventHit): RecentDeviation['severity'] {
  const tags = new Set(event.tags.map((tag) => tag.toLowerCase()));
  if (tags.has('error')) return 'error';
  if (tags.has('alert') || tags.has('alarm')) return 'alert';
  return 'warning';
}

function matchesEventPattern(message: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => message.search(pattern) !== -1);
}

function findDeviationIncidentPattern(message: string) {
  return Object.values(INCIDENT_PATTERNS).find((incidentPattern) =>
    matchesEventPattern(message, incidentPattern.trigger.patterns),
  );
}

async function fetchDeviationTimeline(
  serialNumber: string,
  fromMs: number,
  toMs: number,
): Promise<DeviceEventHit[]> {
  const fetchPage = (page: number) =>
    fetcher.post<GetDeviceEventsBody, GetDeviceEventsResponse>(
      DEVICE_EVENTS_V2_BASE_PATH,
      {
        serialNumber,
        from: String(fromMs),
        to: String(toMs),
        page,
        pageSize: DEVIATION_TIMELINE_PAGE_SIZE,
      },
    );
  const firstPage = await fetchPage(1);
  const totalPages = Math.ceil(firstPage.data.total / DEVIATION_TIMELINE_PAGE_SIZE);
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(0, totalPages - 1) }, (_, index) =>
      fetchPage(index + 2),
    ),
  );

  return [
    ...firstPage.data.items,
    ...remainingPages.flatMap((response) => response.data.items),
  ];
}

export function calculateRecentDeviationDuration(
  deviation: Pick<DeviceEventHit, 'message' | 'timestamp'>,
  timeline: DeviceEventHit[] | undefined,
  currentTimeMs: number,
): RecentDeviation['duration'] {
  const startedAtMs = Date.parse(deviation.timestamp);
  const incidentPattern = findDeviationIncidentPattern(deviation.message);

  if (!Number.isFinite(startedAtMs) || !incidentPattern || !timeline) {
    return { status: 'unknown' };
  }

  const resolution = timeline
    .filter(
      (event) =>
        Date.parse(event.timestamp) > startedAtMs &&
        matchesEventPattern(event.message, incidentPattern.resolution.patterns),
    )
    .sort((left, right) => Date.parse(left.timestamp) - Date.parse(right.timestamp))[0];

  if (resolution) {
    return {
      status: 'resolved',
      milliseconds: Date.parse(resolution.timestamp) - startedAtMs,
    };
  }

  return {
    status: 'ongoing',
    milliseconds: Math.max(0, currentTimeMs - startedAtMs),
  };
}

export function calculateDashboardMonitoringHealth(
  devices: DashboardHealthDeviceState[],
): DashboardMonitoringHealth {
  let alertDevices = 0;
  let warningDevices = 0;
  let unavailableDevices = 0;
  let noCurrentStatusDevices = 0;
  let normalDevices = 0;
  const alertDeviceDetails: DashboardMonitoringHealth['alertDeviceDetails'] = [];
  const warningDeviceDetails: DashboardMonitoringHealth['warningDeviceDetails'] = [];
  const unavailableDeviceDetails: DashboardMonitoringHealth['unavailableDeviceDetails'] =
    [];
  const normalDeviceDetails: DashboardMonitoringHealth['normalDeviceDetails'] = [];

  devices.forEach((device) => {
    const { activityState, aggregateAlertState } = device;
    if (activityState === 'Offline' || activityState === 'Unreachable') {
      unavailableDevices += 1;
      unavailableDeviceDetails.push(device);
      return;
    }

    if (activityState === 'Error' || aggregateAlertState === 'Alert') {
      alertDevices += 1;
      alertDeviceDetails.push(device);
      return;
    }

    if (aggregateAlertState === 'Warn') {
      warningDevices += 1;
      warningDeviceDetails.push(device);
      return;
    }

    if (typeof activityState === 'string' && HEALTHY_ACTIVITY_STATES.has(activityState)) {
      normalDevices += 1;
      normalDeviceDetails.push(device);
      return;
    }

    noCurrentStatusDevices += 1;
  });

  return {
    devicesNeedingAttention: alertDevices + warningDevices + unavailableDevices,
    alertDevices,
    warningDevices,
    unavailableDevices,
    noCurrentStatusDevices,
    normalDevices,
    alertDeviceDetails,
    warningDeviceDetails,
    unavailableDeviceDetails,
    normalDeviceDetails,
  };
}

export function calculateDashboardLabHealth(
  devices: DashboardHealthDeviceState[],
): DashboardLabHealth {
  let healthyDevices = 0;
  let devicesNeedingAttention = 0;
  let unknownDevices = 0;

  devices.forEach(({ activityState, aggregateAlertState }) => {
    const needsAttention =
      (typeof activityState === 'string' &&
        ATTENTION_ACTIVITY_STATES.has(activityState)) ||
      (typeof aggregateAlertState === 'string' &&
        ATTENTION_ALERT_STATES.has(aggregateAlertState));

    if (needsAttention) {
      devicesNeedingAttention += 1;
      return;
    }

    if (
      typeof activityState === 'string' &&
      HEALTHY_ACTIVITY_STATES.has(activityState) &&
      aggregateAlertState === 'Idle'
    ) {
      healthyDevices += 1;
      return;
    }

    unknownDevices += 1;
  });

  const evaluableDevices = healthyDevices + devicesNeedingAttention;

  // TODO: Reevaluate and redefine lab health semantics, then migrate this
  // client-side calculation to a dedicated backend aggregate API.
  const healthPercentage =
    evaluableDevices > 0 ? Math.round((healthyDevices / evaluableDevices) * 100) : null;

  return {
    healthPercentage,
    healthyDevices,
    devicesNeedingAttention,
    unknownDevices,
    pairedDevices: devices.length,
    monitoringHealth: calculateDashboardMonitoringHealth(devices),
  };
}

export function calculateDashboardDeviceStatusSummary(
  devices: DashboardDeviceStatus[],
): DashboardDeviceStatusSummary {
  let connectedDevices = 0;
  let pendingDevices = 0;
  let unavailableDevices = 0;
  const pendingDeviceDetails: DashboardDeviceStatusSummary['pendingDeviceDetails'] = [];
  const unavailableDeviceDetails: DashboardDeviceStatusSummary['unavailableDeviceDetails'] =
    [];

  devices.forEach((device) => {
    const { activityState, connectionStatus } = device;
    if (connectionStatus === 'waitingForMetadata') {
      pendingDevices += 1;
      pendingDeviceDetails.push(device);
      return;
    }

    if (activityState === 'Offline' || !connectionStatus) {
      unavailableDevices += 1;
      unavailableDeviceDetails.push(device);
      return;
    }

    if (connectionStatus === 'paired') {
      connectedDevices += 1;
      return;
    }

    pendingDevices += 1;
    pendingDeviceDetails.push(device);
  });

  return {
    totalDevices: devices.length,
    connectedDevices,
    pendingDevices,
    unavailableDevices,
    pendingDeviceDetails,
    unavailableDeviceDetails,
  };
}

// --- Hooks ---

type DashboardConnectionStatus =
  | 'paired'
  | 'pending'
  | 'readyToPair'
  | 'waitingForMetadata';

async function fetchDeviceCount(
  connectionStatus?: DashboardConnectionStatus,
): Promise<number> {
  const response = await fetcher.get<GetDevicesResponse>(
    createPathWithQueryParams(DEVICE_SERVICE_BASE_PATH, {
      pageSize: 1,
      connectionStatus,
    }),
  );

  return response.pagination?.totalItems ?? 0;
}

async function fetchPairedDevicesPage(page: number): Promise<GetDevicesResponse> {
  return fetcher.get<GetDevicesResponse>(
    createPathWithQueryParams(DEVICE_SERVICE_BASE_PATH, {
      page,
      pageSize: LAB_HEALTH_PAGE_SIZE,
      connectionStatus: 'paired',
    }),
  );
}

async function fetchDevicesPage(page: number): Promise<GetDevicesResponse> {
  return fetcher.get<GetDevicesResponse>(
    createPathWithQueryParams(DEVICE_SERVICE_BASE_PATH, {
      page,
      pageSize: LAB_HEALTH_PAGE_SIZE,
    }),
  );
}

export async function fetchDashboardLabHealth(): Promise<DashboardLabHealth> {
  const firstPage = await fetchPairedDevicesPage(1);
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(0, firstPage.pagination.totalPages - 1) }, (_, index) =>
      fetchPairedDevicesPage(index + 2),
    ),
  );

  return calculateDashboardLabHealth([
    ...firstPage.data,
    ...remainingPages.flatMap((page) => page.data),
  ]);
}

export const useDashboardLabHealth = () =>
  useQuery({
    queryKey: dashboardQueryKeys.labHealth,
    queryFn: fetchDashboardLabHealth,
    refetchInterval: REFETCH_INTERVAL_MS.slow,
  });

export async function fetchDashboardDeviceStatusSummary(): Promise<DashboardDeviceStatusSummary> {
  const firstPage = await fetchDevicesPage(1);
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(0, firstPage.pagination.totalPages - 1) }, (_, index) =>
      fetchDevicesPage(index + 2),
    ),
  );

  return calculateDashboardDeviceStatusSummary([
    ...firstPage.data,
    ...remainingPages.flatMap((page) => page.data),
  ]);
}

export const useDashboardDeviceStatusSummary = () =>
  useQuery({
    queryKey: dashboardQueryKeys.deviceStatusSummary,
    queryFn: fetchDashboardDeviceStatusSummary,
    refetchInterval: REFETCH_INTERVAL_MS.default,
  });

interface DashboardDeviceHistoryParams {
  manufacturer: string;
  serialNumber: string;
  telemetryId: string;
}

export const createDashboardDeviceHistoryQuery = ({
  manufacturer,
  serialNumber,
  telemetryId,
}: DashboardDeviceHistoryParams) => ({
  queryKey: dashboardQueryKeys.deviceHistory(manufacturer, serialNumber, telemetryId),
  queryFn: async (): Promise<GetTelemetryHistoryResponse> => {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - DEVICE_HISTORY_WINDOW_MS);

    return fetcher.get<GetTelemetryHistoryResponse>(
      createPathWithQueryParams(`${DEVICE_SERVICE_BASE_PATH}/${serialNumber}/history`, {
        telemetryId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }),
    );
  },
});

export const useDashboardDeviceHistory = (params: DashboardDeviceHistoryParams) =>
  useQuery({
    ...createDashboardDeviceHistoryQuery(params),
    enabled: Boolean(params.serialNumber && params.telemetryId),
    refetchInterval: REFETCH_INTERVAL_MS.default,
  });

export const useDashboardFleetStatus = () =>
  useQuery({
    queryKey: dashboardQueryKeys.fleetStatus,
    queryFn: async (): Promise<DashboardFleetStatus> => {
      const [
        totalDevices,
        pairedDevices,
        pendingDevices,
        readyToPairDevices,
        waitingForMetadataDevices,
      ] = await Promise.all([
        fetchDeviceCount(),
        fetchDeviceCount('paired'),
        fetchDeviceCount('pending'),
        fetchDeviceCount('readyToPair'),
        fetchDeviceCount('waitingForMetadata'),
      ]);
      const classifiedDevices =
        pairedDevices + pendingDevices + readyToPairDevices + waitingForMetadataDevices;

      return {
        totalDevices,
        pairedDevices,
        pendingDevices,
        readyToPairDevices,
        waitingForMetadataDevices,
        unclassifiedDevices: Math.max(0, totalDevices - classifiedDevices),
      };
    },
    refetchInterval: REFETCH_INTERVAL_MS.default,
  });

export const useRecentDeviations = () =>
  useQuery({
    queryKey: dashboardQueryKeys.recentDeviations,
    queryFn: async (): Promise<RecentDeviations> => {
      const currentTimeMs = Date.now();
      const request: GetDeviceEventsBody = {
        from: String(currentTimeMs - RECENT_DEVIATIONS_WINDOW_MS),
        to: String(currentTimeMs),
        tags: RECENT_DEVIATION_TAGS,
        page: 1,
        pageSize: RECENT_DEVIATIONS_LIMIT,
      };
      const response = await fetcher.post<GetDeviceEventsBody, GetDeviceEventsResponse>(
        DEVICE_EVENTS_V2_BASE_PATH,
        request,
      );
      const timelineStartByDevice = new Map<string, number>();
      response.data.items.forEach((event) => {
        if (!findDeviationIncidentPattern(event.message)) return;

        const timestampMs = Date.parse(event.timestamp);
        if (!Number.isFinite(timestampMs)) return;

        const serialNumber = event.deviceId || event.subject.id;
        const currentStart = timelineStartByDevice.get(serialNumber);
        timelineStartByDevice.set(
          serialNumber,
          currentStart === undefined ? timestampMs : Math.min(currentStart, timestampMs),
        );
      });
      const [metadata, timelineEntries] = await Promise.all([
        fetchDashboardDeviceMetadata(
          response.data.items.map((event) => event.deviceId || event.subject.id),
        ),
        Promise.all(
          [...timelineStartByDevice].map(async ([serialNumber, fromMs]) => {
            try {
              return [
                serialNumber,
                await fetchDeviationTimeline(serialNumber, fromMs, currentTimeMs),
              ] as const;
            } catch {
              return [serialNumber, undefined] as const;
            }
          }),
        ),
      ]);
      const timelines = new Map<string, DeviceEventHit[] | undefined>(timelineEntries);

      return {
        total: response.data.total,
        items: response.data.items.map((event) => {
          const serialNumber = event.deviceId || event.subject.id;
          const device = metadata.get(serialNumber);

          return {
            id: event.sourceEventId,
            severity: getDeviationSeverity(event),
            message: event.message,
            deviceName: device?.name || serialNumber,
            manufacturer: device?.manufacturer || 'eppendorf',
            serialNumber,
            location: device?.location,
            timestamp: event.timestamp,
            duration: calculateRecentDeviationDuration(
              event,
              timelines.get(serialNumber),
              currentTimeMs,
            ),
          };
        }),
      };
    },
    refetchInterval: REFETCH_INTERVAL_MS.fast,
  });

export const useDashboardIncidents = () =>
  useQuery({
    queryKey: dashboardQueryKeys.incidents,
    queryFn: async (): Promise<AlertIncident[]> => {
      const incidents = await fetcher.get<IncidentListDTO[]>(
        createPathWithQueryParams(INCIDENTS_BASE_PATH, {
          isLazyLoading: true,
          pageSize: 6,
          sort: '-createdAt',
        }),
      );

      const metadata = await fetchDashboardDeviceMetadata(
        incidents
          .map((incident) => incident.device?.serialNumber)
          .filter((serialNumber): serialNumber is string => Boolean(serialNumber)),
      );

      return incidents.map((incident) => {
        const primaryEvent = incident.eventMessages.reduce<
          (typeof incident.eventMessages)[number] | undefined
        >((current, event) => {
          if (!current) return event;

          const currentPriority =
            INCIDENT_SEVERITY_PRIORITY[normalizeIncidentSeverity(current.level)];
          const eventPriority =
            INCIDENT_SEVERITY_PRIORITY[normalizeIncidentSeverity(event.level)];

          return eventPriority > currentPriority ? event : current;
        }, undefined);
        const ruleName = incident.notificationRule?.name;
        const serialNumber = incident.device?.serialNumber;
        const device = serialNumber ? metadata.get(serialNumber) : undefined;
        const deviceName =
          device?.name || incident.device?.deviceName || serialNumber || 'Unknown device';

        return {
          id: incident.id,
          severity: normalizeIncidentSeverity(primaryEvent?.level),
          description: primaryEvent?.message || ruleName || 'Incident',
          deviceName,
          location: device?.location,
          timestamp: primaryEvent?.timestamp ?? incident.createdAt ?? 0,
          incidentStatus: incident.incidentStatus,
        };
      });
    },
    refetchInterval: REFETCH_INTERVAL_MS.fast,
  });

interface DashboardReportAccess {
  canReadStabilityReports: boolean;
  canReadAuditTrailReports: boolean;
}

export const useDashboardReports = ({
  canReadStabilityReports,
  canReadAuditTrailReports,
}: DashboardReportAccess) =>
  useQuery({
    queryKey: [
      ...dashboardQueryKeys.reports,
      canReadStabilityReports,
      canReadAuditTrailReports,
    ],
    enabled: canReadStabilityReports || canReadAuditTrailReports,
    queryFn: async (): Promise<{ items: LatestReport[]; partial: boolean }> => {
      const [stabilityReports, auditReports] = await Promise.allSettled([
        canReadStabilityReports
          ? fetcher.get<StabilityReportOutput[]>(
              createPathWithQueryParams(STABILITY_REPORTS_BASE_PATH, {
                limit: 5,
                offset: 0,
                status: 'Ready',
              }),
            )
          : Promise.resolve([]),
        canReadAuditTrailReports
          ? fetcher.get<AuditTrailReportOutput[]>(
              createPathWithQueryParams(AUDIT_TRAIL_REPORTS_BASE_PATH, {
                limit: 5,
                offset: 0,
                status: 'Ready',
              }),
            )
          : Promise.resolve([]),
      ]);

      const stabilityFailed =
        canReadStabilityReports && stabilityReports.status === 'rejected';
      const auditFailed = canReadAuditTrailReports && auditReports.status === 'rejected';
      if (
        (!canReadStabilityReports || stabilityFailed) &&
        (!canReadAuditTrailReports || auditFailed)
      ) {
        throw new Error('Reports could not be loaded.');
      }

      const reports: LatestReport[] = [];

      if (stabilityReports.status === 'fulfilled') {
        reports.push(
          ...stabilityReports.value.map((r) => ({
            id: r.id,
            name: r.name || 'Stability Report',
            createdAt: r.createdAt,
            size: r.fileSize ? formatFileSize(r.fileSize) : '-',
            reportType: 'stability' as const,
          })),
        );
      }

      if (auditReports.status === 'fulfilled') {
        reports.push(
          ...auditReports.value.map((r) => ({
            id: r.id,
            name: r.name || 'Audit Trail Report',
            createdAt: r.createdAt,
            size: r.fileSize ? formatFileSize(r.fileSize) : '-',
            reportType: 'audit-trail' as const,
          })),
        );
      }

      reports.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

      return { items: reports.slice(0, 5), partial: stabilityFailed || auditFailed };
    },
    refetchInterval: REFETCH_INTERVAL_MS.slow,
  });

export const useLatestAuditTrail = () =>
  useQuery({
    queryKey: dashboardQueryKeys.latestAuditTrail,
    queryFn: async (): Promise<EnrichedAuditEvent[]> => {
      const events = await fetcher.get<EnrichedAuditEvent[]>(
        createPathWithQueryParams(AUDIT_BASE_PATH, { pageSize: 5 }),
      );
      return events;
    },
    refetchInterval: REFETCH_INTERVAL_MS.default,
  });

export const usePendingInvitations = () =>
  useQuery({
    queryKey: dashboardQueryKeys.pendingInvitations,
    queryFn: async (): Promise<PendingInvitation[]> => {
      const response = await fetcher.get<PaginatedResponse<User>>(
        createPathWithQueryParams(USERS_BASE_PATH, {
          status: UserStatus['Invite Sent'],
          pageSize: 5,
        }),
      );

      return (response.data ?? []).map((user) => ({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        inviteSentAt: user.inviteSentAt
          ? new Date(user.inviteSentAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '',
      }));
    },
    refetchInterval: REFETCH_INTERVAL_MS.slow,
  });
